import {
  Fragment,
  type ComponentType,
  type FC,
  type ReactNode,
} from 'react';
import { format } from 'date-fns';
import { capitalize } from 'lodash';

import { Smokebreak } from '@wk/components-react16';
import {
  IconAlertCircleFilled,
  IconChevronDown,
  IconChevronUp,
  IconCircleCheckFilled,
  IconCircleMinus,
  IconCircleXFilled,
  IconExternalLink,
} from '@tabler/icons-react';

import { cn, userLocaleTimezone } from '@/lib/utils';
import type { Application, Evaluation } from '@/types';

import {
  viewLabel,
  type EvaluationView,
} from '../../lib/viewAccess';
import type { EvaluationSidebarFilter } from './EvaluationSidebar.types';

interface EvaluationSidebarDetailsPanelProps {
  isOpen: boolean;
  evaluation: Evaluation | null;
  application: Application | null;
  showApplicationDetails?: boolean;
  isPass: boolean;
  isMobileViewport: boolean;
  isMobileDetailsExpanded: boolean;
  onToggleMobileDetailsExpanded: () => void;
  onClose: () => void;
  canSwitchEvaluationView: boolean;
  availableViews: EvaluationView[];
  activeView: EvaluationView;
  onViewChange: (view: EvaluationView) => void;
  filter: EvaluationSidebarFilter;
  onFilterSelect: (filter: EvaluationSidebarFilter) => void;
  checkpointsTreeContent: ReactNode;
  sourceLabel: string;
  SourceIcon: ComponentType<{ className?: string }>;
}

const EvaluationSidebarDetailsPanel: FC<EvaluationSidebarDetailsPanelProps> = ({
  isOpen,
  evaluation,
  application,
  showApplicationDetails = true,
  isPass,
  isMobileViewport,
  isMobileDetailsExpanded,
  onToggleMobileDetailsExpanded,
  onClose,
  canSwitchEvaluationView,
  availableViews,
  activeView,
  onViewChange,
  filter,
  onFilterSelect,
  checkpointsTreeContent,
  sourceLabel,
  SourceIcon,
}) => {
  return (
    <Smokebreak isOpen={isOpen} position="right" onUserRequest={onClose}>
      <h2 slot="smokebreakHeader" className="p-3 text-lg font-semibold">
        Evaluation Details
      </h2>
      <div
        slot="smokebreakBody"
        className="flex h-full min-h-0 w-full flex-col px-4"
      >
        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
          <div className="flex flex-col gap-1">
            <h4 className="text-xs font-medium">Score</h4>
            <p
              className={cn([
                'text-xs font-semibold',
                isPass ? 'text-green-500' : 'text-red-500',
              ])}
            >
              {evaluation?.score}%
            </p>
          </div>
          <div className="flex min-w-0 flex-col gap-1">
            <h4 className="text-xs font-medium">Record ID</h4>
            <p className="break-all text-xs">{evaluation?.id}</p>
          </div>
          <div className="flex flex-col gap-1">
            <h4 className="text-xs font-medium">Generated at</h4>
            <p className="text-xs">
              {format(new Date(`${evaluation?.generatedAt}`), 'yyyy-MM-dd HH:mm')}{' '}
              {userLocaleTimezone()}
            </p>
          </div>
          <div className="flex min-w-0 flex-col gap-1">
            <h4 className="text-xs font-medium">Generated by</h4>
            <p className="break-words text-xs">{evaluation?.generatedBy}</p>
          </div>
          {!isMobileViewport || isMobileDetailsExpanded ? (
            <Fragment>
              {showApplicationDetails ? (
                <>
                  <div className="flex flex-col gap-1">
                    <h4 className="text-xs font-medium">Managed by</h4>
                    <p className="text-xs">{application?.appOps ?? 'N/A'}</p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <h4 className="text-xs font-medium">Hosting model</h4>
                    <p className="text-xs">
                      {application?.hostingModel ?? 'N/A'}
                    </p>
                  </div>
                </>
              ) : null}
              {showApplicationDetails ? (
                <div className="flex flex-col gap-1">
                  <h4 className="text-xs font-medium">
                    All Accounts in WK Tenant?
                  </h4>
                  <p className="text-xs">
                    {evaluation?.isWKTenant != null
                      ? evaluation.isWKTenant
                        ? 'Yes'
                        : 'No'
                      : 'N/A'}
                  </p>
                </div>
              ) : null}
              {showApplicationDetails ? (
                <>
                  <div className="flex flex-col gap-1">
                    <h4 className="text-xs font-medium">Environment</h4>
                    <p className="text-xs">
                      {evaluation?.request?.environmentNames[0]}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <h4 className="text-xs font-medium">Deployment Type</h4>
                    <p className="text-xs">
                      {capitalize(
                        evaluation?.request?.args?.deploymentType as string,
                      )}
                    </p>
                  </div>
                </>
              ) : null}
              {evaluation?.request?.args?.project_name ? (
                <div className="col-span-2 flex min-w-0 flex-col gap-1">
                  <h4 className="text-xs font-medium">Project Name</h4>
                  <p className="break-words text-xs">
                    {evaluation.request.args.project_name as string}
                  </p>
                </div>
              ) : null}
              {evaluation?.request?.args?.repository_names ? (
                <div className="col-span-2 flex min-w-0 flex-col gap-1">
                  <h4 className="text-xs font-medium">Repository Name</h4>
                  <p className="break-words text-xs">
                    {evaluation.request.args.repository_names as string}
                  </p>
                </div>
              ) : null}
              {evaluation?.request?.args?.change_number ? (
                <div className="flex flex-col gap-1">
                  <h4 className="text-xs font-medium">Change Number</h4>
                  <p className="text-xs">
                    {evaluation.request.args.change_number as string}
                  </p>
                </div>
              ) : null}
              <div
                id="evaluation-mobile-additional-details"
                className="flex flex-col gap-1"
              >
                <h4 className="text-xs font-medium">Source</h4>
                <p className="flex items-center gap-2 text-xs">
                  <SourceIcon className="h-4 w-4" />
                  {sourceLabel}
                </p>
              </div>
              {showApplicationDetails &&
              evaluation?.request?.headers['user-agent'] ? (
                <div className="col-span-2 flex min-w-0 flex-col gap-1">
                  <h4 className="text-xs font-medium">User Agent</h4>
                  <p className="break-words text-xs">
                    {evaluation.request.headers['user-agent']}
                  </p>
                </div>
              ) : null}
            </Fragment>
          ) : null}
          {evaluation?.request?.headers['x-orr-automation-definition'] ? (
            <div className="col-span-2 flex min-w-0 flex-col gap-1">
              <h4 className="text-xs font-medium">Automation Definition</h4>
              <a
                className="flex items-center gap-1 break-all text-xs text-blue-500"
                href={evaluation.request.headers['x-orr-automation-definition']}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span>
                  Open in {sourceLabel}
                </span>
                <IconExternalLink size={16} />
              </a>
            </div>
          ) : null}
        </div>
        {isMobileViewport ? (
          <div className="mb-1 mt-1 flex justify-end">
            <button
              type="button"
              className="cursor-pointer p-1"
              aria-label={
                isMobileDetailsExpanded
                  ? 'Collapse additional details'
                  : 'Expand additional details'
              }
              aria-expanded={isMobileDetailsExpanded}
              aria-controls="evaluation-mobile-additional-details"
              onClick={onToggleMobileDetailsExpanded}
            >
              {isMobileDetailsExpanded ? (
                <IconChevronUp size={16} />
              ) : (
                <IconChevronDown size={16} />
              )}
            </button>
          </div>
        ) : null}
        {isMobileViewport ? (
          <div className="evaluationSidebar__mobileCheckpointsActions mt-0 flex flex-1 min-h-0 flex-col pb-4">
            {canSwitchEvaluationView ? (
              <div className="mb-1 w-full">
                <p className="mb-1 text-left text-xs font-semibold text-gray-500">
                  View
                </p>
                <div className="mb-1 flex w-full flex-nowrap items-center justify-start gap-1">
                  {availableViews.map((viewOption) => (
                    <button
                      key={viewOption}
                      type="button"
                      onClick={() => onViewChange(viewOption)}
                      aria-pressed={activeView === viewOption}
                      className={cn([
                        'shrink-0 rounded-full border px-2 py-1 text-[11px] font-semibold leading-4',
                        activeView === viewOption
                          ? 'border-blue-700 bg-blue-50 text-blue-900'
                          : 'border-gray-300 bg-white text-gray-700',
                      ])}
                    >
                      {viewLabel(viewOption)}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
            <div className="mb-1 w-full">
              <p className="mb-1 text-left text-xs font-semibold text-gray-500">
                Status
              </p>
              <div className="flex w-full flex-nowrap items-center justify-start gap-1">
                <button
                  type="button"
                  onClick={() => onFilterSelect('all')}
                  aria-pressed={filter === 'all'}
                  className={cn([
                    'shrink-0 rounded-full border px-2 py-1 text-[11px] font-medium leading-4',
                    filter === 'all'
                      ? 'border-blue-700 bg-blue-50 text-blue-900'
                      : 'border-gray-300 bg-white text-gray-700',
                  ])}
                >
                  <span className="block text-center">All</span>
                </button>
                <button
                  type="button"
                  onClick={() => onFilterSelect('passed')}
                  aria-pressed={filter === 'passed'}
                  className={cn([
                    'shrink-0 rounded-full border px-2 py-1 text-[11px] font-medium leading-4',
                    filter === 'passed'
                      ? 'border-blue-700 bg-blue-50 text-blue-900'
                      : 'border-gray-300 bg-white text-gray-700',
                  ])}
                >
                  <span className="flex items-center justify-center gap-1">
                    <IconCircleCheckFilled className="text-green-500" size={12} />
                    <span>Passed</span>
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => onFilterSelect('not-scored')}
                  aria-pressed={filter === 'not-scored'}
                  className={cn([
                    'shrink-0 rounded-full border px-2 py-1 text-[11px] font-medium leading-4',
                    filter === 'not-scored'
                      ? 'border-blue-700 bg-blue-50 text-blue-900'
                      : 'border-gray-300 bg-white text-gray-700',
                  ])}
                >
                  <span className="flex items-center justify-center gap-1">
                    <IconCircleMinus
                      className="fill-gray-400 [&>path:first-child]:stroke-gray-400 [&>path:last-child]:stroke-white"
                      size={12}
                    />
                    <span>Not Scored</span>
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => onFilterSelect('error')}
                  aria-pressed={filter === 'error'}
                  className={cn([
                    'shrink-0 rounded-full border px-2 py-1 text-[11px] font-medium leading-4',
                    filter === 'error'
                      ? 'border-blue-700 bg-blue-50 text-blue-900'
                      : 'border-gray-300 bg-white text-gray-700',
                  ])}
                >
                  <span className="flex items-center justify-center gap-1">
                    <IconAlertCircleFilled className="text-red-500" size={12} />
                    <span>Error</span>
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => onFilterSelect('failed')}
                  aria-pressed={filter === 'failed'}
                  className={cn([
                    'shrink-0 rounded-full border px-2 py-1 text-[11px] font-medium leading-4',
                    filter === 'failed'
                      ? 'border-blue-700 bg-blue-50 text-blue-900'
                      : 'border-gray-300 bg-white text-gray-700',
                  ])}
                >
                  <span className="flex items-center justify-center gap-1">
                    <IconCircleXFilled className="text-red-500" size={12} />
                    <span>Failed</span>
                  </span>
                </button>
              </div>
            </div>
            <div
              id="evaluation-checkpoints-tree-mobile"
              className="evaluationSidebar__tree mt-1 flex-1 min-h-0 w-full overflow-y-auto pb-6"
            >
              {checkpointsTreeContent}
            </div>
          </div>
        ) : null}
      </div>
    </Smokebreak>
  );
};

export default EvaluationSidebarDetailsPanel;
